import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import {
  Box,
  HStack,
  IconButton,
  Divider,
  Tooltip,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  BiBold,
  BiItalic,
  BiStrikethrough,
  BiHeading,
  BiListUl,
  BiListOl,
  BiTable,
  BiPlus,
  BiMinus,
  BiTrash,
  BiUndo,
  BiRedo,
} from "react-icons/bi";
import { MdFormatQuote, MdCode } from "react-icons/md";
import "./WysiwygEditor.css";

const WysiwygEditor = ({ value, onChange, placeholder = "Enter text..." }) => {
  const containerBorderColor = useColorModeValue("gray.200", "gray.600");
  const toolbarBg = useColorModeValue("gray.50", "gray.700");
  const editorBg = useColorModeValue("white", "gray.800");
  const helperBg = useColorModeValue("gray.50", "gray.700");
  const helperTextColor = useColorModeValue("gray.500", "gray.300");

  const editorTextColor = useColorModeValue("#1A202C", "#E2E8F0");
  const blockquoteBorderColor = useColorModeValue("#E2E8F0", "#4A5568");
  const blockquoteTextColor = useColorModeValue("#718096", "#A0AEC0");
  const inlineCodeBg = useColorModeValue("#EDF2F7", "#2D3748");
  const inlineCodeTextColor = useColorModeValue("#C53030", "#FC8181");
  const codeBlockBg = useColorModeValue("#1A202C", "#171923");
  const codeBlockTextColor = useColorModeValue("#E2E8F0", "#CBD5E0");
  const placeholderTextColor = useColorModeValue("#A0AEC0", "#718096");
  const tableBorderColor = useColorModeValue("#CBD5E0", "#4A5568");
  const tableHeaderBg = useColorModeValue("#F7FAFC", "#2D3748");

  const escapeHtml = (text) =>
    String(text || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const buildHtmlTableFromRows = (rows, withHeaderRow = false) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      return null;
    }

    const maxCols = Math.max(...rows.map((row) => row.length), 0);
    if (maxCols === 0) {
      return null;
    }

    const normalizedRows = rows.map((row) => [
      ...row,
      ...Array(Math.max(0, maxCols - row.length)).fill(""),
    ]);

    if (withHeaderRow) {
      const [headerRow, ...bodyRows] = normalizedRows;
      const headerHtml = `<tr>${headerRow
        .map((cell) => `<th>${escapeHtml(cell)}</th>`)
        .join("")}</tr>`;
      const bodyHtml = bodyRows
        .map(
          (row) =>
            `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`,
        )
        .join("");

      return `<table><thead>${headerHtml}</thead><tbody>${bodyHtml}</tbody></table>`;
    }

    const bodyHtml = normalizedRows
      .map(
        (row) =>
          `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`,
      )
      .join("");

    return `<table><tbody>${bodyHtml}</tbody></table>`;
  };

  const convertClipboardHtmlToTable = (html) => {
    if (!html || !/<table[\s>]/i.test(html)) {
      return null;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const table = doc.querySelector("table");

      if (!table) {
        return null;
      }

      const rowElements = Array.from(table.querySelectorAll("tr"));
      if (rowElements.length === 0) {
        return null;
      }

      const rows = rowElements
        .map((rowElement) =>
          Array.from(rowElement.querySelectorAll("th, td")).map((cell) =>
            String(cell?.textContent || "")
              .replace(/\u00a0/g, " ")
              .trim(),
          ),
        )
        .filter((row) => row.length > 0);

      if (rows.length === 0) {
        return null;
      }

      const hasHeaderRow = !!rowElements[0]?.querySelector("th");
      return buildHtmlTableFromRows(rows, hasHeaderRow);
    } catch {
      return null;
    }
  };

  const convertTabularTextToHtmlTable = (plainText) => {
    if (!plainText || !plainText.includes("\t")) {
      return null;
    }

    const rows = plainText
      .replace(/\r\n?/g, "\n")
      .split("\n")
      .filter((line) => line.trim().length > 0)
      .map((line) => line.split("\t").map((cell) => cell.trim()));

    if (rows.length === 0) {
      return null;
    }

    return buildHtmlTableFromRows(rows, false);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      handlePaste: (_view, event) => {
        const plainText = event?.clipboardData?.getData("text/plain") || "";
        const htmlFromClipboard =
          event?.clipboardData?.getData("text/html") || "";

        const htmlTable = convertClipboardHtmlToTable(htmlFromClipboard);
        if (htmlTable) {
          event.preventDefault();
          editor?.chain().focus().insertContent(htmlTable).run();
          return true;
        }

        const tableHtml = convertTabularTextToHtmlTable(plainText);
        if (!tableHtml) {
          return false;
        }

        event.preventDefault();
        editor?.chain().focus().insertContent(tableHtml).run();
        return true;
      },
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    const nextValue = value || "";
    if (editor.getHTML() !== nextValue) {
      editor.commands.setContent(nextValue, false);
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const ToolButton = ({
    icon,
    onClick,
    label,
    isActive = false,
    isDisabled = false,
  }) => (
    <Tooltip label={label} fontSize="xs">
      <IconButton
        icon={icon}
        onClick={onClick}
        size="sm"
        variant={isActive ? "solid" : "ghost"}
        colorScheme={isActive ? "blue" : "gray"}
        aria-label={label}
        isDisabled={isDisabled}
      />
    </Tooltip>
  );

  return (
    <VStack
      align="stretch"
      spacing={0}
      border="1px solid"
      borderColor={containerBorderColor}
      borderRadius="md"
    >
      {/* Toolbar */}
      <HStack
        p={2}
        bg={toolbarBg}
        borderBottom="1px solid"
        borderColor={containerBorderColor}
        spacing={1}
        wrap="wrap"
      >
        <ToolButton
          icon={<BiBold />}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
          isActive={editor.isActive("bold")}
        />
        <ToolButton
          icon={<BiItalic />}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
          isActive={editor.isActive("italic")}
        />
        <ToolButton
          icon={<BiStrikethrough />}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          label="Strikethrough"
          isActive={editor.isActive("strike")}
        />

        <Divider orientation="vertical" height="24px" />

        <ToolButton
          icon={<BiHeading />}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          label="Heading"
          isActive={editor.isActive("heading", { level: 2 })}
        />
        <ToolButton
          icon={<BiListUl />}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet List"
          isActive={editor.isActive("bulletList")}
        />
        <ToolButton
          icon={<BiListOl />}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Ordered List"
          isActive={editor.isActive("orderedList")}
        />

        <Divider orientation="vertical" height="24px" />

        <ToolButton
          icon={<MdFormatQuote />}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Blockquote"
          isActive={editor.isActive("blockquote")}
        />
        <ToolButton
          icon={<MdCode />}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          label="Code Block"
          isActive={editor.isActive("codeBlock")}
        />

        <Divider orientation="vertical" height="24px" />

        <ToolButton
          icon={<BiTable />}
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          label="Insert Table"
        />
        <ToolButton
          icon={<BiPlus />}
          onClick={() => editor.chain().focus().addRowAfter().run()}
          label="Add Row"
          isDisabled={!editor.can().chain().focus().addRowAfter().run()}
        />
        <ToolButton
          icon={<BiPlus />}
          onClick={() => editor.chain().focus().addColumnAfter().run()}
          label="Add Column"
          isDisabled={!editor.can().chain().focus().addColumnAfter().run()}
        />
        <ToolButton
          icon={<BiMinus />}
          onClick={() => editor.chain().focus().deleteRow().run()}
          label="Delete Row"
          isDisabled={!editor.can().chain().focus().deleteRow().run()}
        />
        <ToolButton
          icon={<BiMinus />}
          onClick={() => editor.chain().focus().deleteColumn().run()}
          label="Delete Column"
          isDisabled={!editor.can().chain().focus().deleteColumn().run()}
        />
        <ToolButton
          icon={<BiTrash />}
          onClick={() => editor.chain().focus().deleteTable().run()}
          label="Delete Table"
          isDisabled={!editor.can().chain().focus().deleteTable().run()}
        />

        <Divider orientation="vertical" height="24px" />

        <ToolButton
          icon={<BiUndo />}
          onClick={() => editor.chain().focus().undo().run()}
          label="Undo"
        />
        <ToolButton
          icon={<BiRedo />}
          onClick={() => editor.chain().focus().redo().run()}
          label="Redo"
        />
      </HStack>

      {/* Editor Content */}
      <Box
        className="wysiwyg-editor"
        p={3}
        minH="120px"
        bg={editorBg}
        _focus={{ outline: "none" }}
        style={{
          "--wys-text": editorTextColor,
          "--wys-blockquote-border": blockquoteBorderColor,
          "--wys-blockquote-text": blockquoteTextColor,
          "--wys-inline-code-bg": inlineCodeBg,
          "--wys-inline-code-text": inlineCodeTextColor,
          "--wys-code-block-bg": codeBlockBg,
          "--wys-code-block-text": codeBlockTextColor,
          "--wys-placeholder-text": placeholderTextColor,
          "--wys-table-border": tableBorderColor,
          "--wys-table-header-bg": tableHeaderBg,
        }}
      >
        <EditorContent
          editor={editor}
          placeholder={placeholder}
          style={{
            minHeight: "120px",
            outline: "none",
          }}
        />
      </Box>

      {/* Helper text */}
      <Box
        p={2}
        fontSize="xs"
        color={helperTextColor}
        bg={helperBg}
        borderTop="1px solid"
        borderColor={containerBorderColor}
      >
        Format your text with rich formatting options
      </Box>
    </VStack>
  );
};

export default WysiwygEditor;
